import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { applyTypography, BaseButton, Chip } from '@repo/common';
import NoticeSection from './NoticeSection';
import ContactSection from './ContactSection';
import ReviewsSection from './ReviewsSection';
import RefundModal from '../Modal/RefundModal';
import CreateNoticeModal from '../Modal/CreateNoticeModal';
import {
  createWorkshopNotice,
  deleteWorkshopNotice,
  toggleWorkshopNoticePin,
  toggleWorkshopNoticeVisibility,
  updateWorkshopNotice,
} from '../../../api/workshop/workshopNotice.api';

const TabSection = ({
  keywords = [],
  details = '',
  notice = '',
  inquiry = '',
  refundPolicy = '',
  workshopId,
  onNoticeCreated,
  /** { averageRating, reviewCount, reviews } — 상위에서 받아 넘긴다 */
  reviewSummary,
  reviewsLoading = false,
}) => {
  const [activeTab, setActiveTab] = useState('details');
  const [isDetailExpanded, setIsDetailExpanded] = useState(false);
  const [isDetailOverflowing, setIsDetailOverflowing] = useState(false);
  const detailContainerRef = useRef(null);
  const [isRefundOpen, setIsRefundOpen] = useState(false);
  const [isCreateNoticeOpen, setIsCreateNoticeOpen] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [previousTab, setPreviousTab] = useState('details');
  const normalizeDetailImages = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (typeof item === 'string') {
            return { src: item, alt: '' };
          }
          if (item && typeof item === 'object') {
            const src = item.url || item.src || item.imageUrl || item.s3Url;
            if (!src) return null;
            return { src, alt: item.alt || item.title || '' };
          }
          return null;
        })
        .filter(Boolean);
    }
    if (typeof value === 'object') {
      const imageList = value.images || value.imageUrls || value.detailImages;
      if (Array.isArray(imageList)) {
        return normalizeDetailImages(imageList);
      }
      const src = value.url || value.src || value.imageUrl || value.s3Url;
      if (src) return [{ src, alt: value.alt || value.title || '' }];
      return [];
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];
      if (/^https?:\/\//i.test(trimmed)) return [{ src: trimmed, alt: '' }];
      return [];
    }
    return [];
  };

  const getDetailText = (value) => {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return '';
      if (/^https?:\/\//i.test(trimmed)) return '';
      return trimmed;
    }
    if (value && typeof value === 'object' && typeof value.text === 'string') {
      return value.text;
    }
    return '';
  };

  const detailImages = normalizeDetailImages(details);
  const detailText = getDetailText(details);
  const DETAIL_MAX_HEIGHT = 800;

  const tabs = [
    { id: 'details', label: 'Details', hasNotification: false },
    { id: 'notice', label: 'Notice', hasNotification: true },
    {
      id: 'reviews',
      // 개수가 있으면 탭에 바로 보여준다 — 눌러보기 전에 후기가 있는지 알 수 있게
      label: reviewSummary?.reviewCount
        ? `Reviews (${reviewSummary.reviewCount})`
        : 'Reviews',
      hasNotification: false,
    },
    { id: 'inquiry', label: 'Contact', hasNotification: false },
    { id: 'refund', label: 'Cancellation & Refund', hasNotification: false },
  ];

  const formatNoticeDate = useCallback((value) => {
    if (!value) return '';
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      const yyyy = value.getFullYear();
      const mm = String(value.getMonth() + 1).padStart(2, '0');
      const dd = String(value.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }

    const raw = String(value).trim();
    if (!raw) return '';
    const directMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (directMatch) return directMatch[1];

    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      const yyyy = parsed.getFullYear();
      const mm = String(parsed.getMonth() + 1).padStart(2, '0');
      const dd = String(parsed.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }

    return raw;
  }, []);

  const normalizeNoticeItem = useCallback(
    (item) => {
      if (typeof item === 'string') {
        const trimmed = item.trim();
        if (!trimmed) return null;
        return {
          title: trimmed,
          date: '',
          content: trimmed,
          isPinned: false,
        };
      }
      if (!item || typeof item !== 'object') return null;

      const title = item.title || item.noticeTitle || '';
      const content = item.content || item.details || item.description || title;
      const date = formatNoticeDate(
        item.date || item.createdAt || item.updatedAt || '',
      );
      const isPinned = Boolean(item.isPinned ?? item.pinned ?? false);

      return {
        ...item,
        title,
        content,
        date,
        isPinned,
      };
    },
    [formatNoticeDate],
  );

  const normalizedNoticesFromProp = useMemo(() => {
    if (Array.isArray(notice)) {
      return notice.map(normalizeNoticeItem).filter(Boolean);
    }
    if (typeof notice === 'string' && notice.trim()) {
      return [normalizeNoticeItem(notice)].filter(Boolean);
    }
    if (notice && typeof notice === 'object') {
      return [normalizeNoticeItem(notice)].filter(Boolean);
    }
    return [];
  }, [notice, normalizeNoticeItem]);

  const [localNoticeItems, setLocalNoticeItems] = useState([]);

  useEffect(() => {
    setLocalNoticeItems(normalizedNoticesFromProp);
  }, [normalizedNoticesFromProp, workshopId]);

  const noticeItems = localNoticeItems;

  const checkDetailOverflow = useCallback(() => {
    const el = detailContainerRef.current;
    if (!el) return;
    if (isDetailExpanded) {
      setIsDetailOverflowing(false);
      return;
    }
    setIsDetailOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [isDetailExpanded]);

  useEffect(() => {
    if (activeTab !== 'details') return;
    checkDetailOverflow();
  }, [
    activeTab,
    detailText,
    detailImages.length,
    isDetailExpanded,
    checkDetailOverflow,
  ]);

  useEffect(() => {
    if (activeTab !== 'details') return undefined;
    const el = detailContainerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => checkDetailOverflow());
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeTab, checkDetailOverflow]);

  useEffect(() => {
    setIsDetailExpanded(false);
  }, [details]);

  const getNoticeId = useCallback((item) => {
    const rawId = item?.id ?? item?.noticeId ?? item?.workshopNoticeId;
    if (rawId === undefined || rawId === null) return null;
    const normalizedId = String(rawId).trim();
    return normalizedId ? rawId : null;
  }, []);

  const handleTabClick = (tabId) => {
    if (tabId === 'refund') {
      setPreviousTab(activeTab);
      setActiveTab('refund');
      setIsRefundOpen(true);
      return;
    }
    setActiveTab(tabId);
  };

  const handleRefundClose = () => {
    setIsRefundOpen(false);
    setActiveTab(previousTab);
  };

  const handleOpenCreateNotice = () => {
    setEditingNotice(null);
    setIsCreateNoticeOpen(true);
  };

  const handleCloseCreateNotice = () => {
    setIsCreateNoticeOpen(false);
    setEditingNotice(null);
  };

  const handleEditNotice = (target, index) => {
    setEditingNotice({ notice: target, index });
    setIsCreateNoticeOpen(true);
  };

  const handleDeleteNotice = async (target, index) => {
    const noticeId = getNoticeId(target);

    try {
      if (workshopId && noticeId !== null) {
        await deleteWorkshopNotice(workshopId, noticeId);
      }
      setLocalNoticeItems((prev) => prev.filter((_, idx) => idx !== index));
      onNoticeCreated?.();
    } catch (error) {
      console.error('Failed to delete notice:', error);
    }
  };

  const handleToggleNoticePin = async (target, index, nextPinned) => {
    const noticeId = getNoticeId(target);

    try {
      if (workshopId && noticeId !== null) {
        await toggleWorkshopNoticePin(workshopId, noticeId, nextPinned);
      }
      setLocalNoticeItems((prev) =>
        prev.map((item, idx) =>
          idx === index
            ? {
                ...item,
                isPinned: nextPinned,
                status: nextPinned ? 'PINNED' : 'ACTIVE',
              }
            : item,
        ),
      );
      onNoticeCreated?.();
    } catch (error) {
      console.error('Failed to toggle notice pin:', error);
    }
  };

  const handleToggleNoticeVisibility = async (target, index) => {
    const noticeId = getNoticeId(target);
    const currentStatus = String(
      target?.status ?? target?.noticeStatus ?? 'ACTIVE',
    ).toUpperCase();
    const shouldShow =
      currentStatus === 'HIDDEN' || currentStatus === 'INACTIVE';
    const nextStatus = shouldShow ? 'ACTIVE' : 'HIDDEN';

    try {
      if (workshopId && noticeId !== null) {
        await toggleWorkshopNoticeVisibility(workshopId, noticeId, shouldShow);
      }
      setLocalNoticeItems((prev) =>
        prev.map((item, idx) =>
          idx === index ? { ...item, status: nextStatus } : item,
        ),
      );
      onNoticeCreated?.();
    } catch (error) {
      console.error('Failed to toggle notice visibility:', error);
    }
  };

  const handleCreateNoticeSubmit = async (payload) => {
    const title = payload?.title?.trim() || '';
    const content = payload?.details?.trim() || '';
    if (!title) return;

    if (editingNotice) {
      try {
        const currentNotice = editingNotice.notice;
        const noticeId = getNoticeId(currentNotice);
        let updatedNotice;

        if (workshopId && noticeId !== null) {
          updatedNotice = await updateWorkshopNotice(workshopId, noticeId, {
            title,
            content,
          });
        }

        const fallbackNotice = {
          ...currentNotice,
          title,
          content,
        };
        const normalizedUpdatedNotice =
          normalizeNoticeItem(updatedNotice || fallbackNotice) ||
          fallbackNotice;

        setLocalNoticeItems((prev) =>
          prev.map((item, idx) =>
            idx === editingNotice.index
              ? { ...item, ...normalizedUpdatedNotice }
              : item,
          ),
        );
        setActiveTab('notice');
        onNoticeCreated?.();
      } catch (error) {
        console.error('Failed to update notice:', error);
      }
      return;
    }

    if (!workshopId) return;

    try {
      const createdNotice = await createWorkshopNotice(workshopId, {
        title,
        content,
        status: 'ACTIVE',
      });

      const normalizedCreatedNotice = normalizeNoticeItem(createdNotice) || {
        title,
        date: formatNoticeDate(new Date()),
        content: content || title,
        isPinned: false,
      };
      setLocalNoticeItems((prev) => [normalizedCreatedNotice, ...prev]);
      setActiveTab('notice');
      onNoticeCreated?.();
    } catch (error) {
      console.error('Failed to create notice:', error);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'details':
        return (
          <div className='flex flex-col items-start min-w-0 w-full tabletH:gap-[24px] tabletV:min-w-[770px]'>
            <div className='flex flex-wrap gap-[8px] items-start relative shrink-0 w-full'>
              {keywords.map((keyword, index) => (
                <Chip
                  key={index}
                  type='fill'
                  state='disabled'
                  hideIcon
                  disableTypography
                  disableDefaultColors
                  bgclass='hidden tabletH:flex h-auto shrink-0 rounded-[8px] bg-grayscale-gray02 px-3 py-[6px] tabletH:h-[38px] tabletH:py-[10px]'
                  borderclass='ring-0'
                  textclass={`${applyTypography('b14Strong')} text-grayscale-gray05`}
                >
                  {keyword}
                </Chip>
              ))}
            </div>
            <div
              ref={detailContainerRef}
              className={`relative w-full ${isDetailExpanded ? '' : 'overflow-hidden'}`}
              style={{
                maxHeight: isDetailExpanded
                  ? undefined
                  : `${DETAIL_MAX_HEIGHT}px`,
              }}
            >
              <div className='flex flex-col gap-[24px] w-full'>
                {detailText && (
                  <p
                    className={`${applyTypography('b14')} tabletH:${applyTypography(
                      'b16',
                    )} text-text-tertiary whitespace-pre-wrap w-full leading-[1.4] tabletH:leading-[1.5]`}
                  >
                    {detailText}
                  </p>
                )}
                {detailImages.length > 0 ? (
                  <div className='flex flex-col gap-[24px] min-w-0 w-full'>
                    {detailImages.map((image, index) => (
                      <div
                        key={`${image.src}-${index}`}
                        className='w-full min-w-0'
                      >
                        <img
                          src={image.src}
                          alt={image.alt || `Workshop detail ${index + 1}`}
                          className='block object-contain w-full h-auto max-w-full'
                          loading='lazy'
                          onLoad={checkDetailOverflow}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  !detailText && (
                    <div className='bg-grayscale-gray01 h-[240px] w-full rounded-[20px]' />
                  )
                )}
              </div>
              {isDetailOverflowing && !isDetailExpanded && (
                <div className='absolute inset-x-0 bottom-0 flex items-end justify-center h-[160px]'>
                  <div
                    className='absolute inset-0 pointer-events-none'
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFF 55%)',
                    }}
                  />
                  <BaseButton
                    type='text'
                    size='m'
                    hideIcon
                    typo={false}
                    isStatic
                    className='relative mb-[24px] h-auto min-w-0 cursor-pointer rounded-none border-0 bg-transparent p-0 hover:opacity-80 transition-opacity'
                    textWrapperClass='h-auto'
                    onClick={() => setIsDetailExpanded(true)}
                  >
                    <span
                      className={`${applyTypography('b14Strong')} tabletH:${applyTypography(
                        'b18Strong',
                      )} text-text-primary leading-[1.4] tabletH:leading-[1.5]`}
                    >
                      See all
                    </span>
                  </BaseButton>
                </div>
              )}
            </div>
          </div>
        );
      case 'notice':
        return (
          <NoticeSection
            notices={noticeItems}
            onCreateNotice={handleOpenCreateNotice}
            onEditNotice={handleEditNotice}
            onToggleNoticeVisibility={handleToggleNoticeVisibility}
            onDeleteNotice={handleDeleteNotice}
            onToggleNoticePin={handleToggleNoticePin}
          />
        );
      case 'inquiry':
        if (typeof inquiry === 'object' && inquiry !== null) {
          return (
            <ContactSection
              phone={inquiry.phone || ''}
              email={inquiry.email || ''}
              chatId={inquiry.chatId || inquiry.openChatId || ''}
            />
          );
        }
        return (
          <ContactSection />
        );
      case 'reviews':
        return (
          <ReviewsSection summary={reviewSummary} loading={reviewsLoading} />
        );
      case 'refund':
        return null;
      default:
        return null;
    }
  };

  return (
    <div className='flex flex-col items-start mb-[30px] pt-0 px-0 relative shrink-0 w-full tabletH:mb-[60px] tabletH:rounded-[50px]'>
      {/* Tabs */}
      <div className='flex items-start overflow-x-auto px-4 py-0 relative rounded-[100px] shrink-0 w-full tabletH:overflow-visible tabletH:pl-0 tabletH:pr-0 tabletV:pr-[50px]'>
        {tabs.map((tab) => (
          <BaseButton
            key={tab.id}
            type='text'
            size='m'
            typo={false}
            isStatic
            hideIcon
            onClick={() => handleTabClick(tab.id)}
            textWrapperClass='h-auto flex-none'
            className={classNames(
              'h-auto min-w-0 cursor-pointer border-0 min-h-px px-[22px] py-[8px] relative rounded-none rounded-tl-[15px] rounded-tr-[15px] shrink-0 tabletH:h-[52px] tabletH:flex-[1_1_0] tabletH:shrink tabletH:max-w-[150px] tabletH:min-w-0 tabletH:px-[10px] tabletH:py-[14px] tabletH:rounded-tl-[30px] tabletH:rounded-tr-[30px] tabletV:max-w-[260px] tabletV:min-w-[180px] tabletV:px-[30px]',
              {
                'bg-grayscale-gray00': activeTab === tab.id,
                'bg-grayscale-gray02': activeTab !== tab.id,
              },
            )}
          >
            {tab.hasNotification ? (
              <div className='flex gap-[2px] items-center justify-center shrink-0 '>
                <p
                  className={classNames(
                    `${applyTypography('b13Strong')} tabletH:${applyTypography(
                      'b16Strong',
                    )} shrink-0 text-center not-italic`,
                    {
                      'text-text-primary': activeTab === tab.id,
                      'text-text-secondary': activeTab !== tab.id,
                    },
                  )}
                >
                  {tab.label}
                </p>
                <div className='flex h-[18px] items-start px-0 py-[4px] relative shrink-0 tabletH:h-[24px]'>
                  <div className='bg-purple-purple04 rounded-[20px] shrink-0 size-[4px] tabletH:size-[6px]' />
                </div>
              </div>
            ) : (
              <p
                className={classNames(
                  `${applyTypography('b13Strong')} tabletH:${applyTypography(
                    'b16Strong',
                  )} shrink-0 text-center not-italic`,
                  {
                    'text-text-primary': activeTab === tab.id,
                    'text-text-secondary': activeTab !== tab.id,
                  },
                )}
              >
                {tab.id === 'refund' ? (
                  <>
                    <span className='tabletV:hidden'>policy</span>
                    <span className='hidden tabletV:inline'>
                      Cancellation & Refund
                    </span>
                  </>
                ) : (
                  tab.label
                )}
              </p>
            )}
          </BaseButton>
        ))}
      </div>

      {/* Content */}
      <div
        className={classNames(
          'bg-grayscale-gray00 flex flex-col items-start px-[16px] py-[30px] relative shrink-0 w-full shadow-3 tabletH:px-[40px] tabletH:py-[40px] tabletH:rounded-bl-[50px] tabletH:rounded-br-[50px] tabletV:px-[80px]',
          {
            'overflow-visible': activeTab === 'notice',
            'overflow-clip': activeTab !== 'notice',
          },
        )}
      >
        {renderContent()}
      </div>

      <RefundModal
        isOpen={isRefundOpen}
        onClose={handleRefundClose}
        content={refundPolicy || ''}
      />

      <CreateNoticeModal
        isOpen={isCreateNoticeOpen}
        onClose={handleCloseCreateNotice}
        onSubmit={handleCreateNoticeSubmit}
        initialTitle={editingNotice?.notice?.title || ''}
        initialDetails={editingNotice?.notice?.content || ''}
        modalTitle={editingNotice ? 'Edit notice' : 'Create a notice'}
        submitLabel={editingNotice ? 'Update' : 'Submit'}
      />
    </div>
  );
};

export default TabSection;
